import i18next from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import type { SupportedLocale, TranslationNamespace } from './types';

// Only bundle en-AU (default) and en-US (fallback) statically.
// All other locales are lazy-loaded on demand via loadLocaleResources().
import enAU_common from './locales/en-AU/common.json';
import enAU_search from './locales/en-AU/search.json';
import enAU_company from './locales/en-AU/company.json';
import enAU_people from './locales/en-AU/people.json';
import enAU_crm from './locales/en-AU/crm.json';
import enAU_subscription from './locales/en-AU/subscription.json';
import enAU_list from './locales/en-AU/list.json';
import enAU_settings from './locales/en-AU/settings.json';
import enAU_browserExt from './locales/en-AU/browser-ext.json';
import enAU_developer from './locales/en-AU/developer.json';
import enAU_dynamicLabels from './locales/en-AU/dynamic-labels.json';

import enUS_common from './locales/en-US/common.json';
import enUS_subscription from './locales/en-US/subscription.json';

const resources = {
  'en-AU': {
    common: enAU_common,
    search: enAU_search,
    company: enAU_company,
    people: enAU_people,
    crm: enAU_crm,
    subscription: enAU_subscription,
    list: enAU_list,
    settings: enAU_settings,
    'browser-ext': enAU_browserExt,
    developer: enAU_developer,
    'dynamic-labels': enAU_dynamicLabels,
  },
  'en-US': {
    common: enUS_common,
    subscription: enUS_subscription,
  },
};

export interface I18nInitOptions {
  locale?: SupportedLocale;
  /** Additional namespaces to load on init beyond 'common' */
  namespaces?: TranslationNamespace[];
  /** Disable browser language detection (useful for tests or SSR) */
  disableDetection?: boolean;
}

export function initI18n(options: I18nInitOptions = {}) {
  const { locale, namespaces = [], disableDetection = false } = options;
  const nsToLoad: TranslationNamespace[] = ['common', 'dynamic-labels', ...namespaces.filter((ns) => ns !== 'common' && ns !== 'dynamic-labels')];

  const instance = i18next.createInstance();

  if (!disableDetection) {
    instance.use(LanguageDetector);
  }

  instance.use(initReactI18next);

  instance.init({
    resources,
    lng: locale ?? undefined,
    fallbackLng: {
      'en-NZ': ['en-AU'],
      'en-SG': ['en-AU'],
      'en-CA': ['en-US', 'en-AU'],
      'en-PH': ['en-US', 'en-AU'],
      'fil-PH': ['en-PH', 'en-US', 'en-AU'],
      'fr-CA': ['en-CA', 'en-US', 'en-AU'],
      'id-ID': ['en-AU'],
      'ms-MY': ['en-AU'],
      'zh-HK': ['en-AU'],
      'ja-JP': ['en-AU'],
      'th-TH': ['en-AU'],
      'vi-VN': ['en-AU'],
      'ko-KR': ['en-AU'],
      default: ['en-AU'],
    },
    ns: nsToLoad,
    defaultNS: 'common',
    fallbackNS: 'common',
    interpolation: {
      escapeValue: false, // React already escapes
    },
    react: {
      useSuspense: false,
    },
    detection: disableDetection
      ? undefined
      : {
          order: ['localStorage', 'navigator'],
          lookupLocalStorage: 'fbl-locale',
          caches: ['localStorage'],
        },
  });

  return instance;
}

/**
 * Lazy-load locale resources on demand.
 * English variants (en-*) fall back to the statically bundled en-AU/en-US,
 * so no async loading is needed for them.
 * For non-English locales, dynamically imports all namespace JSONs in parallel
 * and adds them to the i18next instance via addResourceBundle.
 */
/**
 * Explicit locale loaders — each locale maps to a function that returns all namespace modules.
 * Uses static import paths so bundlers (Parcel/Plasmo, webpack) can resolve them at build time.
 */
const LOCALE_LOADERS: Record<string, () => Promise<Record<string, any>>> = {
  'fr-CA': async () => {
    const [common, search, company, people, crm, subscription, list, settings, developer, dynamicLabels] = await Promise.all([
      import('./locales/fr-CA/common.json'), import('./locales/fr-CA/search.json'), import('./locales/fr-CA/company.json'),
      import('./locales/fr-CA/people.json'), import('./locales/fr-CA/crm.json'), import('./locales/fr-CA/subscription.json'),
      import('./locales/fr-CA/list.json'), import('./locales/fr-CA/settings.json'), import('./locales/fr-CA/developer.json'), import('./locales/fr-CA/dynamic-labels.json'),
    ]);
    return { common, search, company, people, crm, subscription, list, settings, developer, 'dynamic-labels': dynamicLabels };
  },
  'id-ID': async () => {
    const [common, search, company, people, crm, subscription, list, settings, developer, dynamicLabels] = await Promise.all([
      import('./locales/id-ID/common.json'), import('./locales/id-ID/search.json'), import('./locales/id-ID/company.json'),
      import('./locales/id-ID/people.json'), import('./locales/id-ID/crm.json'), import('./locales/id-ID/subscription.json'),
      import('./locales/id-ID/list.json'), import('./locales/id-ID/settings.json'), import('./locales/id-ID/developer.json'), import('./locales/id-ID/dynamic-labels.json'),
    ]);
    return { common, search, company, people, crm, subscription, list, settings, developer, 'dynamic-labels': dynamicLabels };
  },
  'ja-JP': async () => {
    const [common, search, company, people, crm, subscription, list, settings, developer, dynamicLabels] = await Promise.all([
      import('./locales/ja-JP/common.json'), import('./locales/ja-JP/search.json'), import('./locales/ja-JP/company.json'),
      import('./locales/ja-JP/people.json'), import('./locales/ja-JP/crm.json'), import('./locales/ja-JP/subscription.json'),
      import('./locales/ja-JP/list.json'), import('./locales/ja-JP/settings.json'), import('./locales/ja-JP/developer.json'), import('./locales/ja-JP/dynamic-labels.json'),
    ]);
    return { common, search, company, people, crm, subscription, list, settings, developer, 'dynamic-labels': dynamicLabels };
  },
  'ko-KR': async () => {
    const [common, search, company, people, crm, subscription, list, settings, developer, dynamicLabels] = await Promise.all([
      import('./locales/ko-KR/common.json'), import('./locales/ko-KR/search.json'), import('./locales/ko-KR/company.json'),
      import('./locales/ko-KR/people.json'), import('./locales/ko-KR/crm.json'), import('./locales/ko-KR/subscription.json'),
      import('./locales/ko-KR/list.json'), import('./locales/ko-KR/settings.json'), import('./locales/ko-KR/developer.json'), import('./locales/ko-KR/dynamic-labels.json'),
    ]);
    return { common, search, company, people, crm, subscription, list, settings, developer, 'dynamic-labels': dynamicLabels };
  },
  'ms-MY': async () => {
    const [common, search, company, people, crm, subscription, list, settings, developer, dynamicLabels] = await Promise.all([
      import('./locales/ms-MY/common.json'), import('./locales/ms-MY/search.json'), import('./locales/ms-MY/company.json'),
      import('./locales/ms-MY/people.json'), import('./locales/ms-MY/crm.json'), import('./locales/ms-MY/subscription.json'),
      import('./locales/ms-MY/list.json'), import('./locales/ms-MY/settings.json'), import('./locales/ms-MY/developer.json'), import('./locales/ms-MY/dynamic-labels.json'),
    ]);
    return { common, search, company, people, crm, subscription, list, settings, developer, 'dynamic-labels': dynamicLabels };
  },
  'th-TH': async () => {
    const [common, search, company, people, crm, subscription, list, settings, developer, dynamicLabels] = await Promise.all([
      import('./locales/th-TH/common.json'), import('./locales/th-TH/search.json'), import('./locales/th-TH/company.json'),
      import('./locales/th-TH/people.json'), import('./locales/th-TH/crm.json'), import('./locales/th-TH/subscription.json'),
      import('./locales/th-TH/list.json'), import('./locales/th-TH/settings.json'), import('./locales/th-TH/developer.json'), import('./locales/th-TH/dynamic-labels.json'),
    ]);
    return { common, search, company, people, crm, subscription, list, settings, developer, 'dynamic-labels': dynamicLabels };
  },
  'vi-VN': async () => {
    const [common, search, company, people, crm, subscription, list, settings, developer, dynamicLabels] = await Promise.all([
      import('./locales/vi-VN/common.json'), import('./locales/vi-VN/search.json'), import('./locales/vi-VN/company.json'),
      import('./locales/vi-VN/people.json'), import('./locales/vi-VN/crm.json'), import('./locales/vi-VN/subscription.json'),
      import('./locales/vi-VN/list.json'), import('./locales/vi-VN/settings.json'), import('./locales/vi-VN/developer.json'), import('./locales/vi-VN/dynamic-labels.json'),
    ]);
    return { common, search, company, people, crm, subscription, list, settings, developer, 'dynamic-labels': dynamicLabels };
  },
  'zh-HK': async () => {
    const [common, search, company, people, crm, subscription, list, settings, developer, dynamicLabels] = await Promise.all([
      import('./locales/zh-HK/common.json'), import('./locales/zh-HK/search.json'), import('./locales/zh-HK/company.json'),
      import('./locales/zh-HK/people.json'), import('./locales/zh-HK/crm.json'), import('./locales/zh-HK/subscription.json'),
      import('./locales/zh-HK/list.json'), import('./locales/zh-HK/settings.json'), import('./locales/zh-HK/developer.json'), import('./locales/zh-HK/dynamic-labels.json'),
    ]);
    return { common, search, company, people, crm, subscription, list, settings, developer, 'dynamic-labels': dynamicLabels };
  },
  'fil-PH': async () => {
    const [common, search, company, people, crm, subscription, list, settings, developer, dynamicLabels] = await Promise.all([
      import('./locales/fil-PH/common.json'), import('./locales/fil-PH/search.json'), import('./locales/fil-PH/company.json'),
      import('./locales/fil-PH/people.json'), import('./locales/fil-PH/crm.json'), import('./locales/fil-PH/subscription.json'),
      import('./locales/fil-PH/list.json'), import('./locales/fil-PH/settings.json'), import('./locales/fil-PH/developer.json'), import('./locales/fil-PH/dynamic-labels.json'),
    ]);
    return { common, search, company, people, crm, subscription, list, settings, developer, 'dynamic-labels': dynamicLabels };
  },
};

/**
 * Lazy-load locale resources on demand.
 * Uses explicit static import paths (compatible with all bundlers including Plasmo/Parcel).
 */
export async function loadLocaleResources(instance: ReturnType<typeof i18next.createInstance>, locale: SupportedLocale): Promise<void> {
  if (locale.startsWith('en')) return;
  if (instance.hasResourceBundle(locale, 'common')) return;

  const loader = LOCALE_LOADERS[locale];
  if (!loader) return;

  const modules = await loader();
  for (const [namespace, mod] of Object.entries(modules)) {
    instance.addResourceBundle(locale, namespace, mod.default ?? mod, true, true);
  }
}
